import React, { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { WorkspaceKind } from '../../types/workspace';

/**
 * Workspace route guard.
 *
 * It asks the authorization engine whether this identity may enter, rather
 * than inferring it from the path or from a role name. Reaching the URL is not
 * evidence of anything, so a direct navigation, a bookmark and a pasted link
 * all get the same answer.
 *
 * It must sit INSIDE RequireAuth — authentication first, then authorization.
 */
export function RequireWorkspace({
  kind,
  children
}: {
  kind: WorkspaceKind;
  children?: React.ReactNode;
}) {
  const {
    canEnter,
    record
  } = useWorkspace();
  const decision = canEnter(kind);

  /* A refused attempt is recorded exactly like a successful one. */
  useEffect(() => {
    if (!decision.allowed) record('workspace.enter', kind, decision);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, decision.allowed, decision.reason]);
  if (!decision.allowed) {
    return <Navigate to={`/no-access?need=workspace.${kind}`} replace data-id="element-477" data-mp="2ehs5khxpdsgm" />;
  }
  return children ? <>{children}</> : <Outlet data-id="element-478" data-mp="2ngndpzkb5cmk" />;
}

/**
 * Internal administration guard.
 *
 * Separate from the workspace guard on purpose: internal authority is not a
 * workspace, not a public role, and not reachable by holding any membership.
 * It comes only from a provisioned staff link.
 */
export function RequireInternal({
  children
}: {
  children?: React.ReactNode;
}) {
  const {
    internalDecision,
    record
  } = useWorkspace();
  useEffect(() => {
    if (!internalDecision.allowed) {
      record('internal.console.enter', '/admin', internalDecision);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalDecision.allowed, internalDecision.reason]);
  if (!internalDecision.allowed) {
    return <Navigate to="/no-access?need=internal" replace data-id="element-479" data-mp="1bz46zx7iqrj9" />;
  }
  return children ? <>{children}</> : <Outlet data-id="element-480" data-mp="17xslo3vogsgv" />;
}